import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getCustomers } from 'app/entities/customer/customer.reducer';
import { getEntities as getSuppliers } from 'app/entities/supplier/supplier.reducer';
import { OrderStatus } from 'app/shared/model/enumerations/order-status.model';
import { ContractType } from 'app/shared/model/enumerations/contract-type.model';
import { SendEmailStatus } from 'app/shared/model/enumerations/send-email-status.model';
import { createEntity, getEntity, reset, updateEntity } from './customer-order.reducer';

export const CustomerOrderUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const customers = useAppSelector(state => state.customer.entities);
  const suppliers = useAppSelector(state => state.supplier.entities);
  const customerOrderEntity = useAppSelector(state => state.customerOrder.entity);
  const loading = useAppSelector(state => state.customerOrder.loading);
  const updating = useAppSelector(state => state.customerOrder.updating);
  const updateSuccess = useAppSelector(state => state.customerOrder.updateSuccess);
  const orderStatusValues = Object.keys(OrderStatus);
  const contractTypeValues = Object.keys(ContractType);
  const sendEmailStatusValues = Object.keys(SendEmailStatus);

  const handleClose = () => {
    navigate('/customer-order');
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getCustomers({}));
    dispatch(getSuppliers({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    if (values.initialFixedAnnualPrice !== undefined && typeof values.initialFixedAnnualPrice !== 'number') {
      values.initialFixedAnnualPrice = Number(values.initialFixedAnnualPrice);
    }
    if (values.initialFixedMonthlyPrice !== undefined && typeof values.initialFixedMonthlyPrice !== 'number') {
      values.initialFixedMonthlyPrice = Number(values.initialFixedMonthlyPrice);
    }
    values.createdTimestamp = convertDateTimeToServer(values.createdTimestamp);

    const entity = {
      ...customerOrderEntity,
      ...values,
      customer: customers.find(it => it.id.toString() === values.customer?.toString()),
      supplier: suppliers.find(it => it.id.toString() === values.supplier?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          createdTimestamp: displayDefaultDateTime(),
        }
      : {
          orderStatus: 'NEW',
          contractType: 'FIXED',
          checkSendEmail: 'INIT',
          ...customerOrderEntity,
          createdTimestamp: convertDateTimeFromServer(customerOrderEntity.createdTimestamp),
          customer: customerOrderEntity?.customer?.id,
          supplier: customerOrderEntity?.supplier?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="crawlerBackendApp.customerOrder.home.createOrEditLabel" data-cy="CustomerOrderCreateUpdateHeading">
            <Translate contentKey="crawlerBackendApp.customerOrder.home.createOrEditLabel">
              Create or edit a CustomerOrder
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="customer-order-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('crawlerBackendApp.customerOrder.orderDate')}
                id="customer-order-orderDate"
                name="orderDate"
                data-cy="orderDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.customerOrder.desiredDate')}
                id="customer-order-desiredDate"
                name="desiredDate"
                data-cy="desiredDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.customerOrder.orderStatus')}
                id="customer-order-orderStatus"
                name="orderStatus"
                data-cy="orderStatus"
                type="select"
              >
                {orderStatusValues.map(orderStatus => (
                  <option value={orderStatus} key={orderStatus}>
                    {translate(`crawlerBackendApp.OrderStatus.${orderStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.customerOrder.initialFixedAnnualPrice')}
                id="customer-order-initialFixedAnnualPrice"
                name="initialFixedAnnualPrice"
                data-cy="initialFixedAnnualPrice"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.customerOrder.initialFixedMonthlyPrice')}
                id="customer-order-initialFixedMonthlyPrice"
                name="initialFixedMonthlyPrice"
                data-cy="initialFixedMonthlyPrice"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.customerOrder.contractType')}
                id="customer-order-contractType"
                name="contractType"
                data-cy="contractType"
                type="select"
              >
                {contractTypeValues.map(contractType => (
                  <option value={contractType} key={contractType}>
                    {translate(`crawlerBackendApp.ContractType.${contractType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.customerOrder.createdTimestamp')}
                id="customer-order-createdTimestamp"
                name="createdTimestamp"
                data-cy="createdTimestamp"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.customerOrder.depositRequired')}
                id="customer-order-depositRequired"
                name="depositRequired"
                data-cy="depositRequired"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.customerOrder.deposit')}
                id="customer-order-deposit"
                name="deposit"
                data-cy="deposit"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.customerOrder.offerFilePath')}
                id="customer-order-offerFilePath"
                name="offerFilePath"
                data-cy="offerFilePath"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.customerOrder.contractFilePath')}
                id="customer-order-contractFilePath"
                name="contractFilePath"
                data-cy="contractFilePath"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.customerOrder.checkSendEmail')}
                id="customer-order-checkSendEmail"
                name="checkSendEmail"
                data-cy="checkSendEmail"
                type="select"
              >
                {sendEmailStatusValues.map(sendEmailStatus => (
                  <option value={sendEmailStatus} key={sendEmailStatus}>
                    {translate(`crawlerBackendApp.SendEmailStatus.${sendEmailStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                id="customer-order-customer"
                name="customer"
                data-cy="customer"
                label={translate('crawlerBackendApp.customerOrder.customer')}
                type="select"
              >
                <option value="" key="0" />
                {customers
                  ? customers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="customer-order-supplier"
                name="supplier"
                data-cy="supplier"
                label={translate('crawlerBackendApp.customerOrder.supplier')}
                type="select"
              >
                <option value="" key="0" />
                {suppliers
                  ? suppliers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button
                tag={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/customer-order"
                replace
                color="info"
              >
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default CustomerOrderUpdate;
